import jwtService from '../../../services/jwtService/JWTService';
import { getExpressAPIHost } from '../../../../utils';
import { MAX_RECONNECTS, PROGRESS_LOST, RECONNECT_DELAY_MS, parseEvents } from './progressEvents';

const FALLBACK = 'Unable to download the selected documents.';

const readErrorMessage = async (response) => {
    try {
        const body = await response.json();
        return body?.message ?? FALLBACK;
    } catch {
        return FALLBACK;
    }
};

const settled = (message, extra) => Object.assign(new Error(message), { settled: true, ...extra });

const delay = (ms, signal) =>
    new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
        });
    });

const subscribe = async (token, { onProgress, signal }) => {
    const response = await fetch(`${getExpressAPIHost()}/delivery-documents/zip/${token}/progress`, {
        headers: { Accept: 'text/event-stream', ...(await jwtService.getApiAuthHeaders()) },
        signal,
    });
    if (!response.ok) {
        const message = await readErrorMessage(response);
        throw response.status >= 500 ? new Error(message) : settled(message);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffered = '';
    let result = null;

    const handle = (event) => {
        if (event.type === 'error') throw settled(event.message ?? FALLBACK);
        if (event.type === 'done') result = event;
        else onProgress?.(event);
    };

    const pump = async () => {
        const { value, done } = await reader.read();
        if (done) return;
        const { events, rest } = parseEvents(buffered + decoder.decode(value, { stream: true }));
        buffered = rest;
        events.forEach(handle);
        await pump();
    };

    try {
        await pump();
    } finally {
        reader.cancel().catch(() => undefined);
    }
    return result;
};

const watchDeliveryDocumentsDownload = async (token, { onProgress, signal, reconnectDelayMs = RECONNECT_DELAY_MS } = {}) => {
    const attempt = async (reconnects) => {
        let result = null;
        try {
            result = await subscribe(token, { onProgress, signal });
        } catch (err) {
            if (signal?.aborted || err.name === 'AbortError' || err.settled) throw err;
        }
        if (result) return { filename: result.filename, downloaded: result.downloaded ?? 0, skipped: result.skipped ?? [] };
        if (reconnects >= MAX_RECONNECTS) throw settled(PROGRESS_LOST, { lostTrack: true });
        await delay(reconnectDelayMs, signal);
        return attempt(reconnects + 1);
    };
    return attempt(0);
};

export default watchDeliveryDocumentsDownload;
